import * as React from 'react';
import classnames from 'classnames';
import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { AttributeValue, CartLine, CommerceList, Customer, ProductList, ProductPrice, ProductSearchResult, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    IProductInventoryInformation,
    OrderTemplate, getFallbackImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart';
import { ProductListRow } from './product-list-row';
import { useMemo, useState } from 'react';
import PQueue from 'p-queue';
import { ValidateProductAccessResult } from '../../shared-utils/validate-product-access';
import { IProductAgreement, IProductError, ProductSearchResultExt, SimpleProductExt } from '../../shared-utils/types';
import { CountingListLine } from '../../actions/entities/counting-list';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import ProductListRowShimmer from './product-list-row-shimmer';
import { IProductRelationTypeForProduct } from '../../actions/get-relation-types-by-ids';
import { cssCapitalize } from '../../shared-utils/common-utils';
import { OnChangeProductFunction } from '../dropdown/alternative-products';

const dragStateEventName = 'dlvry-product-drag-state';
const defaultGroupKey = 'default';

// Modules where the ProductListComponent is used:
// - Cart
// - Search Result Container
// - Wishlist
// - Order Template

/** Types for getting extended properties only from ProductSearchResultExt and SimpleProductExt */
type ExtendedProperties = & Omit<ProductSearchResultExt | SimpleProductExt, keyof ProductSearchResult | keyof SimpleProduct>;
export type ProductListProduct = ExtendedProperties & Pick<
    ProductSearchResult | SimpleProduct,
    'RecordId' | 'ItemId' | 'Name' | 'PrimaryImageUrl' | 'DefaultUnitOfMeasure' | 'Price'
> & {
    BreakoffProductKey: string;
    InventoryLabel?: string;
    CurrentQuantity?: number;
    CartLine?: CartLine;
    Availability?: IProductInventoryInformation;
    PriceFailed?: boolean;
    DisplayOrder?: number;
    RequestedDeliveryDate?: Date;
    BasePrice?: number;
    LineCreatedDateTime?: Date;
    LineId?: string;
};

export interface IProductListResources {
    buyNowButtonText: string;
    removeButtonText?: string;
    priceErrorText?: string;
    inCartTooltipText?: string;
    deliveryDateLabel?: string;
    transitTooltip?: string;
    agreementProductTooltip?: string;
    addToWishlistText?: string;
    removeFromWishlistText?: string;
    pricePerText?: string;
    priceNotLoggedInText?: string;
    depositSuffixText?: string;
    alternativeProductsButtonText?: string;
    alternativeProductsChangeProductButtonText?: string;
    alternativeProductsLoadingText?: string;
    alternativeProductsNoResultsText?: string;
    alternativeProductsErrorText?: string;
    alternativeProductsCloseButtonText?: string;
}

export interface IActionsAvailable {
    addToCartAction?: boolean;
    addToCartOrderTemplate?: boolean;
    addToWishlistAction?: boolean;
    removeAction?: boolean;
    productComparisonButton?: boolean;
}

interface IProductListItems {
    cart?: ICartState;
    products: ProductListProduct[];
    simpleProducts: SimpleProduct[];
    unitConversions?: UnitOfMeasureConversion[];
    orderTemplates?: OrderTemplate[];
    productErrors?: IProductError[];
    context: ICoreContext;
    resources: IProductListResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    placeholderLines?: number;
    allowMultiSelect?: boolean;
    allowZeroQuantity?: boolean;
    orderTemplate?: OrderTemplate;
    customerInformation?: Customer;
    customerAgreements?: IProductAgreement[];
    brandSchemes?: IBrandSchemeEntity[];
    productComparisonButton?: {} | null | undefined;
    workingProductIds?: number[];
    disableBuyButtons?: boolean;
    disableReorder?: boolean;
    disableQuantityInputForProducts?: number[];
    draggable?: boolean;
    hideUnitConversions?: boolean;
    historicalPrice?: boolean;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    showGroups?: boolean;
    readOnly?: boolean;
    smallerImage?: boolean;
    smallestImage?: boolean;
    validatedProductAccess?: ValidateProductAccessResult[];
    actionsAvailable?: IActionsAvailable;
    breakoffProducts?: { [key: string]: boolean };
    selectedProducts?: IProductSelection;
    wishlists?: CommerceList[];
    relationTypesByIds?: IProductRelationTypeForProduct[];
    alternativeProductsMode?: 'edit' | 'view';
    onProductRemove?: (product: ProductListProduct) => Promise<void>;
    onUpdateOrderTemplates?: (product?: ProductListProduct, orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    onUpdateQuantity?: (product: ProductListProduct, quantity: number, applyImmediately: boolean) => void;
    onUpdateSelection?: (selection: IProductSelection) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (productRecIds: number[], targetRecId: number | undefined, direction: 'over' | 'under') => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
    onChangeProduct?: OnChangeProductFunction;
}

export interface IProductSelection {
    [key: string]: boolean;
}

export interface BuyValidationResult {
    isValid: boolean;
    message?: string;
}

const getRangeBetweenProductIds = (
    products: ProductListProduct[],
    startProductId: number,
    endProductId: number
): ProductListProduct[] => {
    const startIndex = products.findIndex(item => item.RecordId === startProductId);
    const endIndex = products.findIndex(item => item.RecordId === endProductId);

    if (startIndex < 0 || endIndex < 0) {
        return [];
    }

    const [start, end] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
    return products.slice(start, end + 1);
};

const syncSelectionRange = (
    currentSelection: IProductSelection | undefined,
    rangeProducts: ProductListProduct[],
    isSelected: boolean
): IProductSelection => {
    const nextSelection = { ...(currentSelection || {}) };

    rangeProducts.forEach(item => {
        if (!item.RecordId) {
            return;
        }

        if (isSelected) {
            nextSelection[item.RecordId] = true;
        } else {
            delete nextSelection[item.RecordId];
        }
    });

    return nextSelection;
};

const buildGroupedProducts = (
    products: ProductListProduct[],
    showGroups?: boolean
): Record<string, ProductListProduct[]> => products.reduce((acc, product) => {
    const key = showGroups && product.RequestedDeliveryDate
        ? new Date(product.RequestedDeliveryDate).getTime().toString()
        : defaultGroupKey;

    if (!acc[key]) {
        acc[key] = [];
    }

    acc[key].push(product);
    return acc;
}, {} as Record<string, ProductListProduct[]>);

const mapProductsByRecordId = <T extends { RecordId?: number }>(items: T[]): Record<number, T> => items.reduce((acc, item) => {
    if (item.RecordId) {
        acc[item.RecordId] = item;
    }

    return acc;
}, {} as Record<number, T>);

const groupUnitConversionsByProductId = (
    unitConversions?: UnitOfMeasureConversion[]
): Record<number, UnitOfMeasureConversion[]> => (unitConversions || []).reduce((acc, conversion) => {
    if (!conversion.ProductRecordId) {
        return acc;
    }

    if (!acc[conversion.ProductRecordId]) {
        acc[conversion.ProductRecordId] = [];
    }

    acc[conversion.ProductRecordId].push(conversion);
    return acc;
}, {} as Record<number, UnitOfMeasureConversion[]>);

export const ProductListComponent: React.FC<IProductListItems> = ({
    cart,
    products,
    simpleProducts,
    unitConversions,
    orderTemplates,
    productErrors,
    orderTemplate,
    customerInformation,
    customerAgreements,
    brandSchemes,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    placeholderLines,
    allowMultiSelect,
    allowZeroQuantity,
    productComparisonButton,
    readOnly,
    workingProductIds,
    disableBuyButtons,
    disableReorder,
    disableQuantityInputForProducts,
    draggable,
    hideUnitConversions,
    historicalPrice,
    showAvailability,
    showPrices,
    showTotalPrices,
    showCartCount,
    showGroups,
    smallerImage,
    smallestImage,
    relationTypesByIds,
    alternativeProductsMode,
    validatedProductAccess,
    actionsAvailable,
    breakoffProducts,
    selectedProducts,
    wishlists,
    onProductRemove,
    onUpdateOrderTemplates,
    onUpdateQuantity,
    onUpdateSelection,
    onBreakoffChange,
    onDrop,
    onBuyValidate,
    retrieveOrderTemplates,
    onChangeProduct
}) => {
    const [dragOverProduct, setDragOverProduct] = useState<number | undefined>(undefined);
    const [dragOverUnder, setDragOverUnder] = useState<'over' | 'under' | undefined>(undefined);
    const cartQueue = useMemo(() => new PQueue({ concurrency: 1 }), []);
    const selectionAnchorRef = React.useRef<number | undefined>(undefined);
    const listRef = React.useRef<HTMLUListElement | null>(null);
    const selectedProductsRef = React.useRef(selectedProducts);
    const [hoveredProductId, setHoveredProductId] = React.useState<number | undefined>(undefined);
    const [isShiftPressed, setIsShiftPressed] = React.useState(false);

    React.useEffect(() => {
        selectedProductsRef.current = selectedProducts;
    }, [selectedProducts]);

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Shift') {
                setIsShiftPressed(true);
            }
        };
        const handleKeyUp = (event: KeyboardEvent) => {
            if (event.key === 'Shift') {
                setIsShiftPressed(false);
            }
        };
        const handleBlur = () => {
            setIsShiftPressed(false);
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    const selectionPreview = React.useMemo(() => {
        const preview: Record<number, 'select' | 'deselect'> = {};
        const anchorProductId = selectionAnchorRef.current;

        if (!isShiftPressed || !anchorProductId || !hoveredProductId) {
            return preview;
        }

        const anchorIndex = products.findIndex(item => item.RecordId === anchorProductId);
        const hoveredIndex = products.findIndex(item => item.RecordId === hoveredProductId);

        if (anchorIndex < 0 || hoveredIndex < 0) {
            return preview;
        }

        const [start, end] = anchorIndex < hoveredIndex ? [anchorIndex, hoveredIndex] : [hoveredIndex, anchorIndex];
        const previewMode: 'select' | 'deselect' = selectedProducts?.[hoveredProductId] ? 'deselect' : 'select';

        products.slice(start, end + 1).forEach(item => {
            if (item.RecordId) {
                preview[item.RecordId] = previewMode;
            }
        });

        return preview;
    }, [hoveredProductId, isShiftPressed, products, selectedProducts]);

    React.useEffect(() => {
        document.querySelectorAll('.msc-add-to-order-template-icon').forEach(element => {
            element.setAttribute('title', element.getAttribute('aria-label') || '');
        });

        if (!selectedProducts || !onUpdateSelection) {
            return;
        }

        const availableProductIds = new Set(products.map(product => product.RecordId));
        const nextSelection = Object.entries(selectedProducts).reduce((acc, [key, isSelected]) => {
            const productId = Number(key);

            if (!Number.isNaN(productId) && availableProductIds.has(productId) && isSelected) {
                acc[productId] = true;
            }

            return acc;
        }, {} as IProductSelection);

        if (Object.keys(nextSelection).length !== Object.keys(selectedProducts).length) {
            onUpdateSelection(nextSelection);
        }
    }, [onUpdateSelection, products, selectedProducts]);

    React.useEffect(() => {
        let highlightFrame: number | undefined;

        const clearDraggingRows = () => {
            if (highlightFrame !== undefined) {
                cancelAnimationFrame(highlightFrame);
                highlightFrame = undefined;
            }

            listRef.current?.querySelectorAll<HTMLLIElement>('li[data-dragging="true"]').forEach(row => {
                row.removeAttribute('data-dragging');
            });
        };

        const handleDragStateChange = (event: Event) => {
            const detail = (event as CustomEvent<{ active?: boolean; productId?: number }>).detail;

            clearDraggingRows();

            if (!detail?.active || !detail.productId) {
                return;
            }

            const currentSelection = selectedProductsRef.current;
            const highlightedIds = currentSelection?.[detail.productId]
                ? Object.keys(currentSelection)
                    .filter(key => currentSelection[key])
                    .map(key => Number(key))
                    .filter(id => !Number.isNaN(id))
                : [detail.productId];

            highlightFrame = requestAnimationFrame(() => {
                highlightedIds.forEach(productId => {
                    listRef.current?.querySelector<HTMLLIElement>(`li[data-product-id="${productId}"]`)?.setAttribute('data-dragging', 'true');
                });
                highlightFrame = undefined;
            });
        };

        window.addEventListener(dragStateEventName, handleDragStateChange as EventListener);

        return () => {
            window.removeEventListener(dragStateEventName, handleDragStateChange as EventListener);
            clearDraggingRows();
        };
    }, []);

    // Setup navigation with Enter and Shift+Enter for quantity inputs
    React.useEffect(() => {
        const inputs = listRef.current?.querySelectorAll('.dlvry-quantity__quantity-input');

        if (!inputs?.length) {
            return;
        }

        const keyDownHandler = (e: Event) => {
            const event = e as KeyboardEvent;
            const index = Number((event.currentTarget as HTMLElement).getAttribute('data-index'));

            if (event.key === 'Enter') {
                if (event.shiftKey) {
                    if (index === 0) {
                        return;
                    }
                    (inputs.item(index - 1) as HTMLInputElement).select();
                } else {
                    if (index === inputs.length - 1) {
                        return;
                    }
                    (inputs.item(index + 1) as HTMLInputElement).select();
                }
            }
        };

        inputs.forEach((element, index) => {
            element.setAttribute('data-index', index.toString());
            element.addEventListener('keydown', keyDownHandler);
        });

        return () => {
            inputs.forEach(element => {
                element.removeEventListener('keydown', keyDownHandler);
            });
        };
    }, [products]);

    const handleDrop = async (e: React.DragEvent<HTMLElement>, recordId: number | undefined, overUnder: "over" | "under" | undefined) => {
        const dragData = JSON.parse(e.dataTransfer.getData('product'));
        const dragProduct = productsByRecordId[dragData.ProductRecordId];
        const targetProduct = recordId ? productsByRecordId[recordId] : undefined;

        if (!dragProduct || !targetProduct || !overUnder) {
            return;
        }

        const isInSelection = selectedProducts?.[dragProduct.RecordId];
        const productRecIds = isInSelection
            ? products.filter(p => selectedProducts?.[p.RecordId]).map(p => p.RecordId)
            : [dragProduct.RecordId];

        onDrop?.(productRecIds, targetProduct?.RecordId, overUnder);
    };

    const groupedProducts = React.useMemo(() => buildGroupedProducts(products, showGroups), [products, showGroups]);
    const productsByRecordId = React.useMemo(() => mapProductsByRecordId(products), [products]);
    const simpleProductsByRecordId = React.useMemo(() => mapProductsByRecordId(simpleProducts), [simpleProducts]);
    const productErrorsByRecordId = React.useMemo(() => mapProductsByRecordId(productErrors || []), [productErrors]);
    const validatedProductAccessByRecordId = React.useMemo(
        () => mapProductsByRecordId(validatedProductAccess || []),
        [validatedProductAccess]
    );
    const unitConversionsByProductId = React.useMemo(() => groupUnitConversionsByProductId(unitConversions), [unitConversions]);
    const placeholderCount = placeholderLines && placeholderLines > 0 ? Math.max(placeholderLines - 3, 0) : 0;

    return (
        <div>
            <ul
                ref={listRef}
                className={classnames('product-list-component list product-listing')}
                onDrop={async e => {
                    await handleDrop(e, dragOverProduct, 'under');
                }}
                onDragOver={e => {
                    e.stopPropagation();

                    if (!disableReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const firstItem = document.querySelector('.product-list-component > li:first-of-type');
                        const margin = firstItem ? parseFloat(window.getComputedStyle(firstItem).marginBottom) + 6 : 22;
                        const item = document.elementFromPoint(e.clientX, e.clientY - margin);

                        let listItem = item?.parentElement;
                        while (listItem) {
                            if (listItem.tagName === 'LI') {
                                break;
                            }
                            listItem = listItem.parentElement;
                        }

                        if (!listItem) {
                            return;
                        }

                        const productId = listItem?.getAttribute('data-product-id') ? parseFloat(listItem.getAttribute('data-product-id')!) : undefined;

                        setDragOverProduct(productId);
                        setDragOverUnder('under');
                    }
                }}
            >
                {Object.entries(groupedProducts).map(([key, groupProducts], index) => {
                    const items: React.ReactNode[] = [];

                    if (key !== defaultGroupKey) {
                        items.push(
                            <li key={`${key}-group`} className='product-list-group-header'>
                                {resources.deliveryDateLabel}: {context.cultureFormatter.formatDate(new Date(parseFloat(key)), { day: 'numeric', month: 'long' })}
                            </li>
                        );
                    }

                    if (placeholderCount > 0) {
                        for (let i = 0; i < placeholderCount; i++) {
                            items.push(
                                <ProductListRowShimmer
                                    className='product-search-table-row'
                                    key={`${key}-placeholder-${i}`}
                                />
                            );
                        }
                        return items;
                    }

                    for (const product of groupProducts) {
                        const productId = product.RecordId;
                        const simpleProduct = productId ? simpleProductsByRecordId[productId] : undefined;
                        const productIndex = products.findIndex(item => item === product);

                        if (product.Name === 'Loading...') {
                            items.push(
                                <ProductListRowShimmer
                                    className='product-search-table-row'
                                    key={`${product.RecordId}-${product.DefaultUnitOfMeasure}-${product.CartLine?.LineId}`}
                                />
                            );
                            continue;
                        }

                        items.push((
                            <ProductListRow
                                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}-${product.CartLine?.LineId}`}
                                context={context}
                                typeName='product-search-table-row'
                                id={`product-search-table-row-${index}`}
                                className='product-search-table-row'
                                resources={resources}
                                cartQueue={cartQueue}
                                isDragOver={!disableReorder && product.RecordId === dragOverProduct ? dragOverUnder : undefined}
                                isSelected={product.RecordId ? selectedProducts?.[product.RecordId] : false}
                                data={{
                                    cart,
                                    product,
                                    simpleProduct,
                                    orderTemplate,
                                    customerInformation,
                                    customerAgreements,
                                    // `undefined` denotes an in-flight conversion request. Once the request has
                                    // completed, pass an empty array for products without conversions so their
                                    // row does not keep rendering the retrieval shimmer.
                                    unitConversions: productId && unitConversions !== undefined
                                        ? (unitConversionsByProductId[productId] || [])
                                        : undefined,
                                    wishlists,
                                    orderTemplates,
                                    brandSchemes
                                }}
                                productComparisonButton={productComparisonButton}
                                actionsAvailable={actionsAvailable}
                                moduleId={moduleId}
                                moduleType={moduleType}
                                error={productId ? productErrorsByRecordId[productId] : undefined}
                                allowMultiSelect={allowMultiSelect}
                                allowZeroQuantity={allowZeroQuantity}
                                readOnly={readOnly}
                                working={productId ? workingProductIds?.includes(productId) : undefined}
                                disableBuyButton={disableBuyButtons}
                                disableReorder={disableReorder}
                                disableQuantityInput={productId ? disableQuantityInputForProducts?.includes(productId) : undefined}
                                draggable={draggable}
                                hideUnitConversions={hideUnitConversions}
                                historicalPrice={historicalPrice}
                                imageSettings={imageSettings}
                                showAvailability={showAvailability}
                                showPrices={showPrices}
                                showTotalPrices={showTotalPrices}
                                showCartCount={showCartCount}
                                smallerImage={smallerImage}
                                smallestImage={smallestImage}
                                productIndex={productIndex}
                                relationTypesByIds={relationTypesByIds}
                                alternativeProductsMode={alternativeProductsMode}
                                selectionPreview={productId ? selectionPreview[productId] : undefined}
                                initialBreakoffEnabled={Boolean(breakoffProducts?.[product.BreakoffProductKey])}
                                userHasAccess={productId ? validatedProductAccessByRecordId[productId]?.HasAccess : undefined}
                                onUpdateOrderTemplates={onUpdateOrderTemplates}
                                retrieveOrderTemplates={retrieveOrderTemplates}
                                onProductRemove={onProductRemove}
                                onUpdateQuantity={(quantity, applyImmediately) => {
                                    onUpdateQuantity?.(product, quantity, applyImmediately);
                                }}
                                onBreakoffChange={onBreakoffChange}
                                onDrop={handleDrop}
                                onDragOver={(recordId, overUnder) => {
                                    setDragOverProduct(recordId);
                                    setDragOverUnder(overUnder);
                                }}
                                onBuyValidate={onBuyValidate}
                                onChangeProduct={onChangeProduct}
                                onSelectionPreviewHover={setHoveredProductId}
                                onSelectChange={(product, isSelected, shiftKey) => {
                                    const productId = product.RecordId;

                                    if (!productId) {
                                        return;
                                    }

                                    const anchorProductId = selectionAnchorRef.current ?? productId;

                                    if (shiftKey && anchorProductId) {
                                        const rangeProducts = getRangeBetweenProductIds(products, anchorProductId, productId);

                                        if (rangeProducts.length > 0) {
                                            onUpdateSelection?.(syncSelectionRange(selectedProducts, rangeProducts, isSelected));
                                            return;
                                        }
                                    }

                                    const nextSelection = { ...(selectedProducts || {}) };
                                    selectionAnchorRef.current = productId;

                                    if (isSelected) {
                                        nextSelection[productId] = true;
                                    } else {
                                        delete nextSelection[productId];
                                        if (Object.keys(nextSelection).length === 0) {
                                            selectionAnchorRef.current = undefined;
                                        }
                                    }

                                    onUpdateSelection?.(nextSelection);
                                }}
                            />
                        ));
                    }

                    return items;
                })}
            </ul>

        </div>
    );
};

const getInventoryLabel = (
    channelInventoryConfigurationId: number | undefined,
    attributeValues: AttributeValue[] | undefined,
    recordId: number,
    productInventoryStatus?: any
): string | undefined => {
    if (productInventoryStatus) {
        const shippingInventory = productInventoryStatus.find((shipping: any) => shipping.ProductAvailableQuantity.ProductId === recordId);
        if (shippingInventory && shippingInventory.StockLevelLabel) {
            return shippingInventory.StockLevelLabel;
        }
    }
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(attributeValues)) {
        return undefined;
    }
    const inventoryAttribute = attributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
};

export const productSearchResults2ProductList = (
    searchResults: ProductSearchResultExt[],
    channelInventoryConfigurationId: number | undefined,
    cart?: ICartState,
    productInventoryStatus?: IProductInventoryInformation[]
): ProductListProduct[] => {
    if (!searchResults) {
        return [];
    }

    return searchResults.map(searchResult => {
        const cartLine = cart?.cart.CartLines?.find(x => x.ProductId === searchResult.RecordId);
        const productAvailability = productInventoryStatus?.find(x => x.ProductAvailableQuantity.ProductId === searchResult.RecordId);

        return {
            ...searchResult,
            BreakoffProductKey: searchResult.RecordId.toString(),
            InventoryLabel: getInventoryLabel(
                channelInventoryConfigurationId,
                searchResult.AttributeValues,
                searchResult.RecordId,
                productInventoryStatus
            ),
            CurrentQuantity: 0,
            CartLine: cartLine,
            Availability: productAvailability,
            Name: searchResult.Name ? cssCapitalize(searchResult.Name) : undefined
        };
    });
};

export const fullProducts2ProductList = (
    fullProducts: FullProduct[] | undefined,
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => fullProducts?.map(fullProduct => {
    const productDetails = fullProduct.ProductDetails as SimpleProductExt;

    return {
        ...productDetails,
        BreakoffProductKey: productDetails.RecordId.toString(),
        InventoryLabel: productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === productDetails.RecordId
        )?.StockLevelLabel,
        Availability: productAvailabilities?.find(avail => avail.ProductAvailableQuantity.ProductId === productDetails.RecordId),
        Name: productDetails.Name ? cssCapitalize(productDetails.Name) : undefined
    };
}) || [];

export const cartLines2ProductList = (
    cartLines: ICartlinesViewProps[],
    productAvailabilities: IProductInventoryInformation[] | undefined,
    context: ICoreContext,
    activePrices: ProductPrice[] | undefined
): ProductListProduct[] => {
    return cartLines?.map(cartLine => {
        const product = cartLine.data?.product as SimpleProductExt | undefined;
        /* const price = cartLine.data?.cartline.Price || 0; */
        const activePrice = activePrices?.find(x => x.ProductId === product?.RecordId);
        const price = !activePrice ? 0 : cartLine.data?.cartline?.NetAmountWithoutTax ?
            cartLine.data?.cartline.NetAmountWithoutTax / (cartLine.data?.cartline?.Quantity || 1) :
            cartLine.data?.cartline.Price || 0;
        const fallImageUrl = getFallbackImageUrl(product?.ItemId, context.request.apiSettings);
        const productPrice: ProductPrice | undefined = !activePrice ? undefined :{
            ...activePrice || {},
            ...cartLine.data?.cartline || {}
        };
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === product?.RecordId
        );

        return {
            ...product!,
            Availability: availability,
            Badge: getDiscountBadge(productPrice, product?.FallbackBadge),
            BreakoffProductKey: cartLine.data?.cartline.LineId || product!.RecordId.toString(),
            CartLine: cartLine.data?.cartline,
            CurrentQuantity: cartLine.data?.cartline.Quantity,
            InventoryLabel: availability?.StockLevelLabel,
            PrimaryImageUrl: product?.PrimaryImageUrl || fallImageUrl,
            Price: price,
            ProductPrice: productPrice,
            Name: product?.Name ? cssCapitalize(product.Name) : undefined
        };
    }) || [];
};

export const orderTemplateLines2ProductList = (
    lines: CountingListLine[],
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    return lines?.map(line => {
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === line.fullProduct?.ProductDetails.RecordId
        );
        const displayOrder = line.productListLine.ExtensionProperties?.find(prop => prop.Key === 'DISPLAYORDER')?.Value?.DecimalValue;

        const product: ProductListProduct  = {
            ...line.fullProduct!.ProductDetails,
            BreakoffProductKey: line.fullProduct!.ProductDetails.RecordId.toString(),
            DefaultUnitOfMeasure: line.productListLine.UnitOfMeasure,
            InventoryLabel: availability?.StockLevelLabel,
            Availability: availability,
            DisplayOrder: displayOrder,
            CurrentQuantity: line.productListLine.Quantity,
            LineCreatedDateTime: line.productListLine.CreatedDateTime || line.productListLine.LastUpdated,
            BasePrice: line.fullProduct?.ProductDetails.BasePrice,
            PriceFailed: line.PriceFailed,
            LineId: line.productListLine.LineId,
            Name: line.fullProduct?.ProductDetails.Name ? cssCapitalize(line.fullProduct.ProductDetails.Name) : undefined
        };

        if (typeof product.LineCreatedDateTime === 'string') {
            product.LineCreatedDateTime = new Date(product.LineCreatedDateTime);
        }

        return product;
    }) || [];
};
